import { HttpClient, HttpContext } from "@angular/common/http";
import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ProfileDto, ProfileResponse } from "@app/core/user/profile.types";
import { ROUTE } from "@app/utils/routes";
import { environment } from "@environments/environment";
import { finalize, firstValueFrom, from, Observable, of, shareReplay } from "rxjs";
import { SKIP_AUTH_REFRESH } from "./auth.context";

@Injectable({ providedIn: "root" })
export class AuthService {
    private http = inject(HttpClient);
    private router = inject(Router);

    private _user = signal<ProfileDto | null>(
        sessionStorage.getItem("auth_username")
            ? { username: sessionStorage.getItem("auth_username") as string }
            : null
    );
    private _initialized = signal(false);

    public readonly user = this._user.asReadonly();
    public readonly isAuthenticated = computed(() => this._user() !== null);
    public readonly initialized = this._initialized.asReadonly();

    private _refreshInFlight$: Observable<unknown> | null = null;
    private readonly _skipRefresh = new HttpContext().set(SKIP_AUTH_REFRESH, true);

    constructor() {
        effect(() => {
            const user = this._user();
            if (user) {
                sessionStorage.setItem("auth_username", user.username);
            } else {
                sessionStorage.removeItem("auth_username");
            }
        });
    }

    /**
     * Cache hit  → fast path: app starts immediately, session verified in background.
     * No cache   → blocking /profile call (also handles post-SSO redirect).
     */
    init(): Observable<ProfileDto | null> {
        const user = this._user();

        if (user) {
            this._initialized.set(true);
            void this._verifySession();
            return of(user);
        }

        return from(this._requestProfile()).pipe(
            finalize(() => this._initialized.set(true))
        );
    }

    login(): void {
        window.location.href = `${environment.backendUrl}${ROUTE.LOGIN}`;
    }

    refreshOnce(): Observable<unknown> {
        if (!this._refreshInFlight$) {
            this._refreshInFlight$ = this.http
                .get(`${environment.backendUrl}${ROUTE.REFRESH}`, {
                    context: this._skipRefresh
                })
                .pipe(
                    shareReplay({ bufferSize: 1, refCount: true }),
                    finalize(() => (this._refreshInFlight$ = null))
                );
        }
        return this._refreshInFlight$;
    }

    async logout(): Promise<void> {
        await firstValueFrom(
            this.http.post(`${environment.backendUrl}${ROUTE.LOGOUT}`, {})
        ).catch(() => undefined);
        this._user.set(null);
        this.router.navigate(["/login"]);
    }

    silentLogout(): void {
        this._user.set(null);
        this.router.navigate(["/login"]);
    }

    /** Background session check. Calls silentLogout on final failure. */
    private async _verifySession(): Promise<void> {
        const data = await this._requestProfile();
        if (!data) this.silentLogout();
    }

    /** Fetches /profile with automatic refresh retry on 401. Sets _user on success, returns null on final failure. */
    private async _requestProfile(): Promise<ProfileDto | null> {
        const profileUrl = `${environment.backendUrl}${ROUTE.PROFILE}`;

        try {
            const response = await firstValueFrom(
                this.http.get<ProfileResponse>(profileUrl, { context: this._skipRefresh })
            );
            this._user.set(response.data);
            return response.data;
        } catch {
            try {
                await firstValueFrom(this.refreshOnce());
                const response = await firstValueFrom(
                    this.http.get<ProfileResponse>(profileUrl, { context: this._skipRefresh })
                );
                this._user.set(response.data);
                return response.data;
            } catch {
                return null;
            }
        }
    }
}
